import { useState, useEffect, useCallback } from 'react';
import { Header } from '../components/Layout';
import { ConfigPanel } from '../components/Config';
import { api } from '../hooks/useApi';
import { clsx } from 'clsx';
import {
  Thermometer, Droplets, Wind, Power, Settings, RefreshCw,
  ThermometerSun, ThermometerSnowflake, Gauge, Activity
} from 'lucide-react';

const COOLING_MODES = [
  { value: 0, label: 'Auto', description: 'Automaatne temperatuurikontroll' },
  { value: 1, label: 'Manual', description: 'Manuaalne juhtimine' },
  { value: 2, label: 'Off', description: 'Jahutus väljas' }
];

function CoolingPanel() {
  const [cooling, setCooling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coolSetpoint, setCoolSetpoint] = useState(25);
  const [heatSetpoint, setHeatSetpoint] = useState(15);

  const loadCooling = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getCooling();
      setCooling(data);
      if (data.setpoint_cool) setCoolSetpoint(data.setpoint_cool);
      if (data.setpoint_heat) setHeatSetpoint(data.setpoint_heat);
    } catch (err) {
      console.error('Failed to load cooling:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCooling();
    const interval = setInterval(loadCooling, 10000);
    return () => clearInterval(interval);
  }, [loadCooling]);

  const handleToggle = async () => {
    try {
      setSaving(true);
      await api.setCoolingEnabled(!cooling.enabled);
      await loadCooling();
    } catch (err) {
      console.error('Failed to toggle cooling:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = async (mode) => {
    try {
      setSaving(true);
      await api.setCoolingMode(mode);
      await loadCooling();
    } catch (err) {
      console.error('Failed to set mode:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSetpointSave = async () => {
    try {
      setSaving(true);
      await api.setCoolingSetpoint(coolSetpoint, heatSetpoint);
      await loadCooling();
    } catch (err) {
      console.error('Failed to set setpoint:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !cooling) {
    return (
      <div className="card p-6">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-700 rounded"></div>
          <div className="h-4 bg-gray-700 rounded w-48"></div>
        </div>
      </div>
    );
  }

  if (!cooling || cooling.error) {
    return (
      <div className="card p-6">
        <div className="flex items-center gap-3 text-gray-400">
          <Droplets className="w-6 h-6" />
          <span>Liquid cooling andmed pole saadaval</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Droplets className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-gray-200">Liquid Cooling</h3>
        </div>
        <button onClick={loadCooling} disabled={loading} className="text-gray-400 hover:text-white">
          <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
        </button>
      </div>

      <div className="p-4 space-y-6">
        {/* Status and Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={clsx(
              'w-12 h-12 rounded-lg flex items-center justify-center',
              cooling.enabled ? 'bg-blue-500/20' : 'bg-gray-700'
            )}>
              <Power className={clsx('w-6 h-6', cooling.enabled ? 'text-blue-400' : 'text-gray-500')} />
            </div>
            <div>
              <div className="text-white font-medium">
                {cooling.enabled ? 'Aktiivne' : 'Väljas'}
              </div>
              <div className="text-sm text-gray-400">
                Režiim: {COOLING_MODES.find(m => m.value === cooling.mode)?.label || 'Tundmatu'}
              </div>
            </div>
          </div>

          <button
            onClick={handleToggle}
            disabled={saving}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              cooling.enabled
                ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            )}
          >
            {cooling.enabled ? 'Lülita välja' : 'Lülita sisse'}
          </button>
        </div>

        {/* Temperature readings */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
              <ThermometerSnowflake className="w-4 h-4" />
              Sisselask
            </div>
            <div className="text-2xl font-bold text-blue-400">
              {cooling.temp_inlet?.toFixed(1)}°C
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
              <ThermometerSun className="w-4 h-4" />
              Väljund
            </div>
            <div className="text-2xl font-bold text-orange-400">
              {cooling.temp_outlet?.toFixed(1)}°C
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
              <Thermometer className="w-4 h-4" />
              Väljatõmme
            </div>
            <div className="text-2xl font-bold text-gray-300">
              {cooling.temp_exhaust?.toFixed(1)}°C
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
              <Wind className="w-4 h-4" />
              Ümbritsev
            </div>
            <div className="text-2xl font-bold text-green-400">
              {cooling.temp_ambient?.toFixed(1)}°C
            </div>
          </div>
        </div>

        {/* Pressure and pump */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
              <Gauge className="w-4 h-4" />
              Rõhk sisse
            </div>
            <div className="text-xl font-bold text-gray-300">
              {cooling.pressure_inlet?.toFixed(2)} bar
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
              <Gauge className="w-4 h-4" />
              Rõhk välja
            </div>
            <div className="text-xl font-bold text-gray-300">
              {cooling.pressure_outlet?.toFixed(2)} bar
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
              <Activity className="w-4 h-4" />
              Pump
            </div>
            <div className="text-xl font-bold text-gray-300">
              {cooling.pump_speed?.toFixed(0)}%
              {cooling.compressor_on && (
                <span className="ml-2 text-sm text-green-400">Kompr.</span>
              )}
            </div>
          </div>
        </div>

        {/* Mode selection */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Töörežiim</label>
          <div className="grid grid-cols-3 gap-2">
            {COOLING_MODES.map(mode => (
              <button
                key={mode.value}
                onClick={() => handleModeChange(mode.value)}
                disabled={saving}
                className={clsx(
                  'p-3 rounded-lg text-left transition-colors',
                  cooling.mode === mode.value
                    ? 'bg-blue-500/20 border border-blue-500'
                    : 'bg-gray-700/50 border border-gray-700 hover:bg-gray-700'
                )}
              >
                <div className="font-medium text-white">{mode.label}</div>
                <div className="text-xs text-gray-400">{mode.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Setpoints */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Jahutuse seadepunkt (°C)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={coolSetpoint}
                onChange={(e) => setCoolSetpoint(parseFloat(e.target.value))}
                className="input flex-1"
                min="15"
                max="35"
                step="0.5"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Kütte seadepunkt (°C)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={heatSetpoint}
                onChange={(e) => setHeatSetpoint(parseFloat(e.target.value))}
                className="input flex-1"
                min="5"
                max="25"
                step="0.5"
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleSetpointSave}
          disabled={saving}
          className="btn btn-primary w-full"
        >
          {saving ? 'Salvestamine...' : 'Salvesta seadepunktid'}
        </button>
      </div>
    </div>
  );
}

export function SettingsPage({ wsConnected }) {
  return (
    <div className="h-full flex flex-col">
      <Header
        title="Seadistused"
        connected={wsConnected}
      />

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Liquid Cooling Panel */}
          <CoolingPanel />

          {/* Config Panel */}
          <ConfigPanel />
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
